import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";
import { Plus } from "lucide-react";

/**
 * FAQ Generic Component
 * Renders a collapsible/expandable FAQ section with title, description, and multiple Q&A items
 */
const FAQGeneric = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    title = "",
    description = "",
    faqItems = [],
    component_name = "faq_generic",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localDescription, setLocalDescription] = useState(description);
  const [localFAQItems, setLocalFAQItems] = useState(faqItems);
  // In preview mode, expand all items (email clients don't support JavaScript)
  // In edit mode, only first item expanded by default
  const initialFAQItems = Array.isArray(faqItems) && faqItems.length > 0
    ? faqItems
    : [];
  const [expandedItems, setExpandedItems] = useState(
    previewMode 
      ? new Set(initialFAQItems.map((_, i) => i)) // All expanded in preview
      : new Set([0]) // First item expanded in edit mode
  );

  useEffect(() => {
    setLocalTitle(title);
    setLocalDescription(description);
    setLocalFAQItems(faqItems);
    // Update expanded items when preview mode or faqItems change
    const currentItems = Array.isArray(faqItems) && faqItems.length > 0 ? faqItems : localFAQItems;
    if (previewMode) {
      setExpandedItems(new Set(currentItems.map((_, i) => i)));
    } else if (currentItems.length > 0) {
      setExpandedItems(new Set([0]));
    }
  }, [id, title, description, faqItems, previewMode]);

  const toggleItem = (index) => {
    // Only allow toggling in edit mode, not preview mode
    if (!previewMode && !isEditing) {
      const newExpanded = new Set(expandedItems);
      if (newExpanded.has(index)) {
        newExpanded.delete(index);
      } else {
        newExpanded.add(index);
      }
      setExpandedItems(newExpanded);
    }
  };

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      title: localTitle,
      description: localDescription,
      faqItems: localFAQItems,
    });
    setIsEditing(false);
  };

  const addFAQItem = () => {
    setLocalFAQItems([
      ...localFAQItems,
      {
        question: "Ipsum is simply dummy text of the printing and typesetting industry?",
        answer: "Duis convallis neque sed eros vehicula vestibulum ut a orci. Aliquam sed nibh nec metus porta porta. Integer justo nisi, venenatis quis dapibus vel, auctor efficitur nibh. Vestibulum accumsan augue eu enim vehicula, vel interdum ligula",
      },
    ]);
  };

  const removeFAQItem = (index) => {
    const newItems = localFAQItems.filter((_, i) => i !== index);
    setLocalFAQItems(newItems);
    const newExpanded = new Set(expandedItems);
    newExpanded.delete(index);
    setExpandedItems(newExpanded);
  };

  const updateFAQItem = (index, field, value) => {
    const newItems = [...localFAQItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };
    setLocalFAQItems(newItems);
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component faq-generic-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit FAQ Section</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Title</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Frequently asked questions"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <textarea
            className="form-control"
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="Enter description"
            rows={3}
          />
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label fw-bold mb-0">FAQ Items</label>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={addFAQItem}
            >
              <Plus size={16} className="me-1" />
              Add FAQ Item
            </button>
          </div>

          {localFAQItems.map((item, index) => (
            <div
              key={index}
              className="border rounded p-3 mb-3"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>FAQ Item {index + 1}</strong>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removeFAQItem(index)}
                >
                  Remove
                </button>
              </div>
              <div className="mb-2">
                <label className="form-label small">Question</label>
                <input
                  className="form-control form-control-sm"
                  value={item.question || ""}
                  onChange={(e) =>
                    updateFAQItem(index, "question", e.target.value)
                  }
                  placeholder="Enter question"
                />
              </div>
              <div>
                <label className="form-label small">Answer</label>
                <textarea
                  className="form-control form-control-sm"
                  value={item.answer || ""}
                  onChange={(e) =>
                    updateFAQItem(index, "answer", e.target.value)
                  }
                  placeholder="Enter answer"
                  rows={3}
                />
              </div>
            </div>
          ))}

          {localFAQItems.length === 0 && (
            <div className="text-center p-4 border rounded" style={{ backgroundColor: "#f8f9fa" }}>
              <p className="text-muted mb-2">No FAQ items yet</p>
              <button
                className="btn btn-sm btn-primary"
                onClick={addFAQItem}
              >
                <Plus size={16} className="me-1" />
                Add First FAQ Item
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  return (
    <>
      {!previewMode && (
        <div className="email-component faq-generic mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tr>
          <td
            align="left"
            style={{
              padding: "32px 16px",
              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
            }}
          >
            {/* Title */}
            <div
              style={{
                fontSize: "28px",
                fontWeight: "bold",
                color: primaryColor || "#333333",
                marginBottom: "16px",
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              {localTitle}
            </div>

            {/* Description */}
            {localDescription && (
              <div
                style={{
                  fontSize: "16px",
                  color: primaryColor || "#333333",
                  marginBottom: "24px",
                  lineHeight: "1.6",
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                {localDescription}
              </div>
            )}

            {/* FAQ Items - Email Compatible Table Layout */}
            {localFAQItems.length > 0 ? (
              <table
                width="100%"
                cellPadding="0"
                cellSpacing="0"
                border="0"
                style={{
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                  marginTop: "16px",
                }}
              >
                {localFAQItems.map((item, index) => {
                  const isExpanded = expandedItems.has(index);
                  return (
                    <React.Fragment key={index}>
                      <tr>
                        <td
                          style={{
                            padding: index > 0 ? "12px 0 0 0" : "0",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <table
                            width="100%"
                            cellPadding="0"
                            cellSpacing="0"
                            border="0"
                            style={{
                              backgroundColor: "#ffffff",
                              border: "1px solid #e0e0e0",
                              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                            }}
                          >
                            {/* Question Row */}
                            <tr>
                              <td
                                style={{
                                  padding: "16px 20px",
                                  backgroundColor: "#fafafa",
                                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                  cursor: previewMode ? "default" : "pointer",
                                }}
                                onClick={() => {
                                  if (!previewMode) {
                                    toggleItem(index);
                                  }
                                }}
                              >
                                <table
                                  width="100%"
                                  cellPadding="0"
                                  cellSpacing="0"
                                  border="0"
                                  style={{
                                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                  }}
                                >
                                  <tr>
                                    <td
                                      width="100%"
                                      style={{
                                        fontSize: "16px",
                                        fontWeight: "500",
                                        color: primaryColor || "#333333",
                                        paddingRight: "16px",
                                        fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                      }}
                                    >
                                      {item.question || "Question"}
                                    </td>
                                    <td
                                      align="right"
                                      valign="middle"
                                      style={{
                                        fontSize: "20px",
                                        color: secondaryColor || "#a1894c",
                                        fontWeight: "bold",
                                        whiteSpace: "nowrap",
                                        fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                      }}
                                    >
                                      {(isExpanded || previewMode) ? "−" : "+"}
                                    </td>
                                  </tr>
                                </table>
                              </td>
                            </tr>

                            {/* Answer (Collapsible) - Always show in preview mode for email compatibility */}
                            {(isExpanded || previewMode) && (
                              <tr>
                                <td
                                  style={{
                                    padding: "16px 20px",
                                    borderTop: "1px solid #e0e0e0",
                                    fontSize: "14px",
                                    color: primaryColor || "#333333",
                                    lineHeight: "1.6",
                                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                                  }}
                                >
                                  {item.answer || "Answer"}
                                </td>
                              </tr>
                            )}
                          </table>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </table>
            ) : (
              <table
                width="100%"
                cellPadding="0"
                cellSpacing="0"
                border="0"
                style={{
                  fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                }}
              >
                <tr>
                  <td
                    align="center"
                    style={{
                      padding: "24px",
                      color: primaryColor || "#999999",
                      fontStyle: "italic",
                      fontSize: "14px",
                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    }}
                  >
                    No FAQ items available
                  </td>
                </tr>
              </table>
            )}
          </td>
        </tr>
      </table>
    </>
  );
};

export default FAQGeneric;

